import { useTranslation } from "react-i18next";

import type { components } from "@/api/types";
import { formatMoney } from "@/shared/lib";

type MyRating = components["schemas"]["MyRating"];

/**
 * Собственная позиция — единственный блок экрана с яркой зелёной границей.
 *
 * Показывается всегда, даже когда в топ не попал: место может быть далеко за
 * таблицей, и «вас тут нет» на экране рейтинга — худший из возможных ответов.
 *
 * `rank === null` означает, что участия пока нет вовсе: роботов не куплено, и
 * ранжировать нечего. Тогда вместо номера стоит прочерк, а не «#0».
 */
export function RatingMyPosition({ me }: { me: MyRating }) {
  const { t } = useTranslation();

  return (
    <section
      className={[
        "grid grid-cols-3 items-center gap-2 rounded-md border border-status-profit/60 px-4 py-3",
        "bg-[linear-gradient(180deg,color-mix(in_srgb,var(--color-status-profit)_6%,var(--palette-panel)),var(--palette-bg))]",
        "shadow-[inset_0_0_25px_color-mix(in_srgb,var(--color-status-profit)_3%,transparent)]",
      ].join(" ")}
    >
      <div className="flex flex-col gap-1">
        <span className="text-caption text-text-secondary">{t("rating.myPosition")}</span>
        <span className="text-h2 font-bold text-status-profit">
          {me.rank === null ? t("rating.noPosition") : `#${String(me.rank)}`}
        </span>
      </div>

      <span className="text-center text-small text-text-secondary">
        {t("rating.robotsCount", { count: me.robots_count })}
      </span>

      <span className="text-right text-small font-semibold text-status-profit">
        {formatMoney(me.earned, { sign: "always", suffix: "USDT" })}
      </span>
    </section>
  );
}
